/**
 * サポートチャットウィジェット（support-agent loader.js）連携
 *
 * - 設定は SUPPORT_WIDGET_URL / SUPPORT_WIDGET_TENANT_ID（env.*.js → nuxt.config.js env）
 * - loader.js は window.supportWidgetConfig を読み込み時に1回だけ読む（後から差し替え不可）
 * - 生成物は document.body 直下の <iframe>（id なし、src が SUPPORT_WIDGET_URL で始まる）
 * 設計書: docs/plans/2026-09-18-cmp-support-widget-design.md
 */

import { currentUserAgent, isIosAppUserAgent } from '~/utils/userAgent'

export const LOADER_PATH = '/loader.js'

// webpack の DefinePlugin は `process.env.FOO` という「プロパティアクセスの字面」だけを
// ビルド時に文字列リテラルへ置換する。`process.env` をオブジェクトごと渡すと置換されず
// クライアントでは空オブジェクトになるため、必ずここで個別に取り出しておく。
export const WIDGET_URL = process.env.SUPPORT_WIDGET_URL
export const WIDGET_TENANT_ID = process.env.SUPPORT_WIDGET_TENANT_ID

// ウィジェットの重なり順。loader.js は iframe にインラインで z-index:9999 を付けるが、
// それだと CMP のモーダル（b-modal 1040/1050・v-dialog 2001）より上に出てしまい、
// モーダル側のフォーカス保持でチャットに入力できないのに画面だけ覆う状態になる。
// 画面の固定要素（保存バー 5・ヘッダー等 〜20）より上、モーダル・スマホの絞り込みシート（1040〜）・
// トースト（2000）・メディアビューア（2100）・確認ダイアログ（10000）・ローディング（10001）より下に置く。
export const SUPPORT_WIDGET_Z_INDEX = 1035

// CMP 側で当てるスタイル。loader のインライン指定より優先させるため !important を付ける。
// 表示・非表示（launcher: hidden の display:none）は loader に任せるので display は触らない。
// 開いたときの PC カード表示は高さ 652px 固定なので、低い画面で上にはみ出さないよう max-height を付ける
// （SP 幅 480px 以下は loader が全画面表示にするので当てない）。
function hostStyleCss(url) {
  const sel = `iframe[src^="${url}/"]`
  return [
    `${sel} { z-index: ${SUPPORT_WIDGET_Z_INDEX} !important; }`,
    `@media (min-width: 481px) { ${sel} { max-height: calc(100vh - 40px) !important; } }`,
  ].join('\n')
}

/**
 * URL と tenantId が両方設定されていれば有効（テスト用に引数で上書きできる）。
 * ただし iOS アプリ内では無効にする。iOS アプリは iframe の読み込みまで SFSafariViewController に
 * 飛ばしてしまう（gyms_ios の decidePolicyFor が isMainFrame を見ていない）ため。iOS 側修正後に外す。
 */
export function isSupportWidgetEnabled({
  url = WIDGET_URL,
  tenantId = WIDGET_TENANT_ID,
  userAgent = currentUserAgent(),
} = {}) {
  if (isIosAppUserAgent(userAgent)) return false
  return Boolean(url && tenantId)
}

/** 「本部名 / 店舗名 / スタッフ名」。欠けている要素は飛ばす */
export function buildCustomerName({ headquarter, company, staff } = {}) {
  return [headquarter?.name, company?.name, staff?.name].filter(Boolean).join(' / ')
}

/** メニューから呼ぶ。loader 未読込・無効時は何もしない */
export function openSupportWidget(win = window) {
  win.SupportWidget?.open?.()
}

/**
 * loader.js の注入と後片付け。
 * store に依存させず window / document を注入できるようにしてテスト可能にしている。
 */
export function createSupportWidgetController({
  url = WIDGET_URL,
  tenantId = WIDGET_TENANT_ID,
  win,
  doc,
}) {
  let loadedKey = null // 注入済みの識別子 "companyUid:staffUid"
  let scriptEl = null
  let styleEl = null

  const identityKey = (auth) => {
    const companyUid = auth?.company?.uid
    const staffUid = auth?.staff?.uid
    return companyUid && staffUid ? `${companyUid}:${staffUid}` : null
  }

  function teardown() {
    doc.querySelectorAll(`iframe[src^="${url}"]`).forEach((el) => el.remove())
    if (scriptEl) {
      scriptEl.remove()
      scriptEl = null
    }
    if (styleEl) {
      styleEl.remove()
      styleEl = null
    }
    delete win.__supportWidgetLoaded
    delete win.__supportWidgetStubQueue
    delete win.SupportWidget
    delete win.supportWidgetConfig
    loadedKey = null
  }

  function inject(auth) {
    const key = identityKey(auth)
    if (!key || key === loadedKey) return
    if (loadedKey) teardown() // 別アカウントに切り替わった

    win.supportWidgetConfig = {
      tenantId,
      customerId: auth.company.uid,
      // スタッフ単位で会話履歴を分け、誰の問い合わせかを会話ごとに記録するため（support-agent SUP-454）
      staffId: auth.staff.uid,
      customerName: buildCustomerName(auth),
      launcher: 'hidden',
    }
    styleEl = doc.createElement('style')
    styleEl.setAttribute('data-support-widget-host', '')
    styleEl.textContent = hostStyleCss(url)
    doc.head.appendChild(styleEl)

    scriptEl = doc.createElement('script')
    scriptEl.src = `${url}${LOADER_PATH}`
    scriptEl.async = true
    scriptEl.onerror = () => console.warn('[supportWidget] loader.js の読み込みに失敗しました')
    doc.body.appendChild(scriptEl)
    loadedKey = key
  }

  return { inject, teardown }
}
